import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Edit3, X, Music, Mic2, Film, BookOpen, Crown, DollarSign, BarChart3, Headphones, Info, Wallet, Check, Upload, Calendar, Landmark, ExternalLink, Unplug, Megaphone } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth } from "../contexts";
import { PageHeader, EmptyState } from "../components/ui-bits";
import { confirmDialog } from "../components/ConfirmModal";
import { StreamLinksFields } from "../components/StreamLinks";
import { PromoKitModal } from "../components/PromoKit";
import { toast } from "sonner";

const TYPES = [
  { key: "mixtapes", label: "Mixtapes", icon: Music },
  { key: "singles",  label: "Singles",  icon: Mic2 },
  { key: "videos",   label: "Videos",   icon: Film },
  { key: "books",    label: "Books",    icon: BookOpen },
];

export default function StudioPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mixtapes");
  const [content, setContent] = useState({ mixtapes: [], singles: [], videos: [], books: [] });
  const [earnings, setEarnings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const canUpload = isAdmin || (user?.is_artist && user?.verified);

  const load = useCallback(() => {
    if (!canUpload) return;
    setBusy(true);
    api.get("/my/content")
      .then((r) => setContent(r.data))
      .catch(() => {})        // RBAC / network errors fall through silently
      .finally(() => setBusy(false));
    api.get("/my/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  }, [canUpload]);
  useEffect(() => {
    queueMicrotask(load);
  }, [load]);

  if (!user) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
        <p className="font-mono-label text-neutral-500 mb-4">Sign in to access your Studio.</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }
  if (!canUpload) {
    const requested = user?.verification_requested;
    return (
      <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-20 text-center" data-testid="studio-verification-required">
        <div className="card-brut border-[var(--accent)] bg-[#1a0a05] p-10">
          <Mic2 size={36} className="mx-auto text-[var(--accent)] mb-4" />
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">ROLE-BASED ACCESS</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase mb-4" data-testid="verification-required-headline">
            Verification Required
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg mb-2" data-testid="verification-required-message">
            Upgrade to a Verified Artist Account to upload music and videos.
          </p>
          <p className="font-mono-label text-[10px] text-neutral-500 mb-8">
            Only Admins and Verified Artists can publish releases. Standard members can stream, tip, shop, and book talent.
          </p>
          {requested ? (
            <div className="font-mono-label text-[10px] text-[var(--accent)] uppercase tracking-widest" data-testid="verification-pending-pill">
              ✓ Verification Pending Review
            </div>
          ) : (
            <Link to={`/artist/${user.username}`} className="btn-primary" data-testid="go-request-verification-btn">
              Request Artist Verification
            </Link>
          )}
        </div>
      </div>
    );
  }

  const isEarnings = tab === "earnings";
  const items = isEarnings ? [] : (content[tab] || []);
  const remove = async (id, title) => {
    const ok = await confirmDialog({
      title: "Delete release?",
      message: `"${title || "This release"}" will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/my/content/${tab}/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed. Please try again.");
    }
  };

  return (
    <div className="pb-32" data-testid="studio-page">
      <PageHeader kicker="THE STUDIO" title="ARTIST CONTROL ROOM" sub={`Welcome back, ${user.display_name}. Manage release metadata, watch your tips, and track stream payouts.`} />
      <div className="divider-tape" />

      <div className="border-b border-neutral-800 sticky top-[68px] bg-black/95 backdrop-blur-xl z-20">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto" data-testid="studio-tabs">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`font-mono-label text-[11px] px-4 py-4 inline-flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-neutral-400 hover:text-white"
              }`}
              data-testid={`studio-tab-${t.key}`}
            >
              <t.icon size={12} /> {t.label.toUpperCase()} <span className="text-neutral-500">{(content[t.key] || []).length}</span>
            </button>
          ))}
          <button
            onClick={() => setTab("earnings")}
            className={`font-mono-label text-[11px] px-4 py-4 inline-flex items-center gap-2 border-b-2 transition-colors ${
              isEarnings ? "border-[var(--accent)] text-white" : "border-transparent text-neutral-400 hover:text-white"
            }`}
            data-testid="studio-tab-earnings"
          >
            <BarChart3 size={12} /> EARNINGS
          </button>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
        {isEarnings ? (
          <EarningsPanel earnings={earnings} onChange={load} />
        ) : (
          <>
            <div className="flex justify-between mb-6 items-center">
              <h2 className="font-headline text-2xl uppercase">Your {tab}</h2>
              <button onClick={() => setEditing({})} className="btn-primary" data-testid={`studio-new-${tab}`}>
                <Plus size={14} /> New {tab.slice(0, -1)}
              </button>
            </div>

            <div className="mb-6 border border-neutral-800 bg-neutral-950 p-4 flex gap-3 items-start" data-testid="studio-upload-notice">
              <Info size={16} className="text-[var(--accent)] shrink-0 mt-0.5" />
              <div className="font-mono-label text-[10px] text-neutral-400 leading-relaxed">
                FULL FILE UPLOADS NOW LIVE FOR ARTISTS: <span className="text-white">COVER · PNG/JPEG</span>, <span className="text-white">AUDIO · MP3/WAV/M4A</span>, <span className="text-white">VIDEO · MP4/MOV</span>, <span className="text-white">BOOK · PDF</span>, <span className="text-white">MEDIA KIT · ZIP</span>. NEED HELP? <Link to="/messages?to=admin" className="text-[var(--accent)] underline">MESSAGE ADMIN</Link>.
              </div>
            </div>

            {busy ? (
              <p className="font-mono-label text-[10px] text-neutral-500">Loading…</p>
            ) : items.length === 0 ? (
              <EmptyState>No {tab} yet. Hit &ldquo;New {tab.slice(0, -1)}&rdquo; to drop your first one.</EmptyState>
            ) : (
              <div className="border border-neutral-800" data-testid={`studio-${tab}-list`}>
                {items.map((it) => (
                  <div key={it.id} className="flex items-center gap-4 p-3 border-b border-neutral-800 last:border-0" data-testid={`studio-item-${it.id}`}>
                    {it.cover_image && <img src={resolveFileUrl(it.cover_image)} alt="" className="w-12 h-12 object-cover border border-neutral-800" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-headline uppercase truncate flex items-center gap-2">
                        {it.title}
                        {it.is_official && <span className="chip chip-accent">OFFICIAL</span>}
                        {it.featured && <span className="chip">FEATURED</span>}
                        {it.is_indy && <span className="chip">INDY</span>}
                        {it.early_access && <span className="chip chip-accent inline-flex items-center gap-1"><Crown size={9} /> EARLY</span>}
                      </div>
                      <div className="font-mono-label text-[10px] text-neutral-400">
                        {it.genre} · {it.year} · {it.plays || 0} plays
                      </div>
                    </div>
                    {tab !== "books" && (
                      <button onClick={() => setPromo(it)} className="font-mono-label text-[10px] px-3 py-2 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)] inline-flex items-center gap-1" data-testid={`studio-promo-kit-${it.id}`}>
                        <Megaphone size={12} /> <span className="hidden sm:inline">PROMO KIT</span>
                      </button>
                    )}
                    <button onClick={() => setEditing(it)} className="text-neutral-300 hover:text-white p-2" data-testid={`studio-edit-${it.id}`}><Edit3 size={14} /></button>
                    <button onClick={() => remove(it.id, it.title)} className="text-neutral-300 hover:text-[var(--accent)] p-2" data-testid={`studio-delete-${it.id}`}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {editing && (
        <StudioForm
          ctype={tab}
          initial={editing}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
      {promo && <PromoKitModal ctype={tab} item={promo} onClose={() => setPromo(null)} />}
    </div>
  );
}

function ConnectPayoutsCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api.get("/connect/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  // Handle Stripe return: ?connect=return on /studio means user came back
  // from Stripe-hosted onboarding. Refresh status to pick up latest flags.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return") {
      refresh();
      // Clean the URL so a refresh doesn't replay the side-effect.
      const url = new URL(window.location.href);
      url.searchParams.delete("connect");
      url.searchParams.delete("mock");
      window.history.replaceState({}, "", url.toString());
    }
  }, [refresh]);

  const onboard = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("origin_url", window.location.origin);
      const { data } = await api.post("/connect/onboard", fd);
      // Real Stripe link → external redirect. Dev-mock link is internal — just refresh.
      if (data.dev_mock) {
        await refresh();
      } else {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Stripe onboarding.");
    } finally { setBusy(false); }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.post("/connect/dashboard-link");
      if (data.url && data.url !== "#mock-dashboard") {
        window.open(data.url, "_blank", "noopener");
      } else {
        toast.info("Dashboard link unavailable in dev-mock mode. Add a real STRIPE_API_KEY to unlock.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open Stripe dashboard.");
    }
  };

  const disconnect = async () => {
    const ok = await confirmDialog({
      title: "Disconnect Stripe payouts?",
      message: "Future tips and bookings will collect into the platform wallet until you reconnect. Existing payouts in progress are unaffected.",
      confirmLabel: "Disconnect",
      destructive: true,
    });
    if (!ok) return;
    await api.post("/connect/disconnect");
    refresh();
  };

  if (!status) {
    return (
      <div className="card-brut p-6 border-neutral-800" data-testid="connect-payouts-loading">
        <p className="font-mono-label text-[10px] text-neutral-500">Loading payout connection…</p>
      </div>
    );
  }

  const connected = !!status.connected;
  const live = connected && status.charges_enabled && status.payouts_enabled;
  const partial = connected && !live;

  return (
    <div
      className={`card-brut p-6 sm:p-7 ${live ? "border-[var(--accent)] bg-gradient-to-br from-[#1a0a05] to-black" : "border-neutral-800"}`}
      data-testid="connect-payouts-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
        <div className="min-w-0">
          <div className={`font-mono-label text-[10px] mb-2 flex items-center gap-2 ${live ? "text-[var(--accent)]" : "text-neutral-400"}`}>
            <Landmark size={12} /> STRIPE CONNECT · DIRECT PAYOUTS
          </div>
          {!connected && (
            <>
              <h3 className="font-headline text-2xl uppercase mb-2" data-testid="connect-status-headline">Connect Your Bank</h3>
              <p className="text-neutral-300 text-sm max-w-xl">
                Onboard with Stripe Express to receive booking + tip payouts directly to your bank account. Takes ~2 minutes — Stripe handles ID + tax forms.
              </p>
            </>
          )}
          {partial && (
            <>
              <h3 className="font-headline text-2xl uppercase mb-2" data-testid="connect-status-headline">Finish Stripe Onboarding</h3>
              <p className="text-neutral-300 text-sm max-w-xl">
                You started onboarding but Stripe still needs more info before they&apos;ll release payouts.
                {status.dev_mock && <span className="block mt-1 text-neutral-500">(Dev-mock mode — add a real STRIPE_API_KEY to go live.)</span>}
              </p>
              <div className="flex gap-3 mt-3 font-mono-label text-[10px]">
                <span className={status.charges_enabled ? "text-[var(--accent)]" : "text-neutral-500"}>{status.charges_enabled ? "✓" : "○"} CHARGES</span>
                <span className={status.payouts_enabled ? "text-[var(--accent)]" : "text-neutral-500"}>{status.payouts_enabled ? "✓" : "○"} PAYOUTS</span>
                <span className={status.details_submitted ? "text-[var(--accent)]" : "text-neutral-500"}>{status.details_submitted ? "✓" : "○"} DETAILS</span>
              </div>
            </>
          )}
          {live && (
            <>
              <h3 className="font-headline text-2xl uppercase mb-2 text-[var(--accent)]" data-testid="connect-status-headline">
                <Check size={18} className="inline mr-1" /> Payouts Live
              </h3>
              <p className="text-neutral-300 text-sm max-w-xl">
                Tips and bookings now route straight to your bank via Stripe Connect.
                {status.dev_mock && <span className="block mt-1 text-neutral-500">(Dev-mock — payouts are simulated until a real STRIPE_API_KEY is set.)</span>}
              </p>
              <div className="font-mono-label text-[10px] text-neutral-500 mt-3 truncate" data-testid="connect-acct-id">
                ACCOUNT · {status.stripe_account_id}
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {!connected && (
            <button onClick={onboard} disabled={busy} className="btn-primary" data-testid="connect-onboard-btn">
              <Landmark size={14} /> {busy ? "Opening Stripe…" : "Connect Payouts"}
            </button>
          )}
          {partial && (
            <button onClick={onboard} disabled={busy} className="btn-primary" data-testid="connect-continue-btn">
              <ExternalLink size={14} /> {busy ? "Opening…" : "Continue Onboarding"}
            </button>
          )}
          {live && (
            <>
              <button onClick={openDashboard} className="btn-primary" data-testid="connect-dashboard-btn">
                <ExternalLink size={14} /> Stripe Dashboard
              </button>
              <button onClick={disconnect} className="btn-ghost" data-testid="connect-disconnect-btn">
                <Unplug size={14} /> Disconnect
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EarningsPanel({ earnings, onChange }) {
  const [payoutOpen, setPayoutOpen] = useState(false);
  if (!earnings) {
    return <p className="font-mono-label text-[10px] text-neutral-500">Loading earnings…</p>;
  }
  const fmt = (n) => `$${Number(n).toFixed(2)}`;
  const canRequest = (earnings.available_balance || 0) >= (earnings.min_payout || 10);
  return (
    <div className="space-y-10" data-testid="studio-earnings">
      {/* Stripe Connect direct-payouts card */}
      <ConnectPayoutsCard />

      {/* Payout balance hero card */}
      <div className="card-brut p-6 sm:p-8 border-[var(--accent)] bg-gradient-to-br from-[#1a0a05] to-black" data-testid="studio-payout-card">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
              <Wallet size={12} /> AVAILABLE FOR PAYOUT
            </div>
            <div className="font-display text-6xl sm:text-7xl text-[var(--accent)] leading-none" data-testid="payout-available">
              {fmt(earnings.available_balance)}
            </div>
            <div className="font-mono-label text-[10px] text-neutral-400 mt-3">
              MIN PAYOUT {fmt(earnings.min_payout)} · PAID OUT {fmt(earnings.paid_out)} · PENDING {fmt(earnings.pending_payout)}
            </div>
          </div>
          <button
            onClick={() => setPayoutOpen(true)}
            disabled={!canRequest}
            className="btn-primary"
            data-testid="payout-request-btn"
            title={canRequest ? "Request a payout" : `Reach ${fmt(earnings.min_payout)} to request a payout`}
          >
            <Wallet size={14} /> Request Payout
          </button>
        </div>
      </div>

      {/* Earnings summary stat grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="TOTAL TIPS" value={fmt(earnings.tip_total)} sub={`${earnings.tip_count} tippers`} icon={DollarSign} accent />
        <Stat label="STREAMS" value={Number(earnings.streams).toLocaleString()} sub="all-time plays" icon={Headphones} />
        <Stat label="STREAM PAYOUT (EST.)" value={fmt(earnings.stream_earnings)} sub={`@ $${earnings.stream_rate.toFixed(3)} / play`} icon={BarChart3} />
        <Stat label="BOOKINGS NET" value={fmt(earnings.bookings_total)} sub={`${earnings.bookings_count} paid · $${Number(earnings.bookings_fee_total || 0).toFixed(2)} platform`} icon={Calendar} />
        <Stat label="COMBINED" value={fmt(earnings.combined_earnings)} sub="tips + streams + bookings" icon={Crown} accent />
      </div>

      <section data-testid="studio-supporter-summary">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">CULTURE PASS SUPPORTERS</div>
        <p className="text-neutral-300 text-sm">
          <span className="font-headline text-3xl text-[var(--accent)]">{earnings.pass_supporter_tip_count}</span>
          <span className="ml-3 align-middle">of your tips came from Culture Pass members.</span>
        </p>
      </section>

      {/* Payout history */}
      {earnings.payouts?.length > 0 && (
        <section>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">PAYOUT HISTORY</div>
          <div className="border border-neutral-800" data-testid="studio-payout-history">
            {earnings.payouts.map((p) => (
              <div key={p.id} className="p-3 border-b border-neutral-800 last:border-0 flex justify-between items-center" data-testid={`payout-row-${p.id}`}>
                <div>
                  <div className="font-display text-xl">${Number(p.amount).toFixed(2)}</div>
                  <div className="font-mono-label text-[10px] text-neutral-500">{new Date(p.created_at).toLocaleString()} · {p.method?.toUpperCase()}</div>
                </div>
                <span className={`chip ${p.status === "paid" || p.status === "approved" ? "chip-accent" : ""}`}>{(p.status || "pending").toUpperCase()}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">RECENT TIPS</div>
        {earnings.recent_tips?.length ? (
          <div className="border border-neutral-800" data-testid="studio-recent-tips">
            {earnings.recent_tips.map((t) => (
              <div key={t.id} className="p-3 border-b border-neutral-800 last:border-0 flex justify-between items-center">
                <div>
                  <div className="font-display text-xl">${Number(t.amount).toFixed(0)}</div>
                  {t.message && <div className="text-neutral-300 text-sm italic">&ldquo;{t.message}&rdquo;</div>}
                </div>
                <div className="font-mono-label text-[10px] text-neutral-500 text-right">
                  {new Date(t.created_at).toLocaleDateString()}{t.is_pass_supporter && <div className="text-[var(--accent)]">VERIFIED SUPPORTER</div>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>No tips yet — share your profile so fans can support.</EmptyState>
        )}
      </section>

      <section data-testid="studio-recent-bookings-section">
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">RECENT BOOKING PAYOUTS</div>
        {earnings.recent_bookings?.length ? (
          <div className="border border-neutral-800" data-testid="studio-recent-bookings">
            <div className="grid grid-cols-[1fr_120px_100px_100px] gap-3 px-3 py-2 border-b border-neutral-800 font-mono-label text-[9px] text-neutral-500 bg-neutral-950">
              <div>CLIENT</div>
              <div>GROSS</div>
              <div>PLATFORM</div>
              <div className="text-right">NET</div>
            </div>
            {earnings.recent_bookings.map((b) => (
              <div key={b.id} className="grid grid-cols-[1fr_120px_100px_100px] gap-3 items-center px-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`booking-row-${b.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {b.client?.avatar ? (
                    <img src={resolveFileUrl(b.client.avatar)} alt="" className="w-8 h-8 object-cover border border-neutral-800" />
                  ) : (
                    <div className="w-8 h-8 border border-neutral-800 bg-neutral-900 flex items-center justify-center"><Calendar size={12} /></div>
                  )}
                  <div className="min-w-0">
                    <div className="font-headline uppercase text-sm truncate">{b.client?.display_name || "—"}</div>
                    <div className="font-mono-label text-[9px] text-neutral-500 truncate">
                      {b.event_type ? `${b.event_type.toUpperCase()} · ` : ""}{b.event_date ? new Date(b.event_date).toLocaleDateString() : new Date(b.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="font-display text-base">${Number(b.gross || 0).toFixed(0)}</div>
                <div className="font-mono-label text-[10px] text-neutral-500">-${Number(b.platform_fee || 0).toFixed(2)} ({Number(b.platform_fee_pct || 0).toFixed(0)}%)</div>
                <div className="font-display text-lg text-right text-[var(--accent)]">${Number(b.amount || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState>No paid bookings released yet — accept a booking and have the client pay + mark complete.</EmptyState>
        )}
      </section>

      <section>
        <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">PER-RELEASE REVENUE</div>
        {(() => {
          const flat = Object.entries(earnings.by_type || {}).flatMap(([ct, items]) =>
            items.map((it) => ({ ...it, ct }))
          ).sort((a, b) => (b.plays || 0) - (a.plays || 0));
          if (flat.length === 0) {
            return <EmptyState>No releases yet — drop content from any tab to start earning.</EmptyState>;
          }
          return (
            <div className="border border-neutral-800" data-testid="studio-per-release">
              <div className="grid grid-cols-[1fr_80px_80px_80px] gap-3 px-3 py-2 border-b border-neutral-800 font-mono-label text-[9px] text-neutral-500 bg-neutral-950">
                <div>RELEASE</div>
                <div>TYPE</div>
                <div className="text-right">PLAYS</div>
                <div className="text-right">REVENUE</div>
              </div>
              {flat.slice(0, 15).map((it) => (
                <div key={`${it.ct}-${it.id}`} className="grid grid-cols-[1fr_80px_80px_80px] gap-3 items-center px-3 py-2 border-b border-neutral-800 last:border-0" data-testid={`release-row-${it.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    {it.cover_image && <img src={resolveFileUrl(it.cover_image)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />}
                    <div className="font-headline text-sm uppercase truncate">{it.title}</div>
                  </div>
                  <div className="font-mono-label text-[10px] text-neutral-400">{it.ct.toUpperCase()}</div>
                  <div className="font-display text-lg text-right">{(it.plays || 0).toLocaleString()}</div>
                  <div className="font-display text-lg text-right text-[var(--accent)]">${Number(it.revenue || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
          );
        })()}
      </section>

      {payoutOpen && (
        <PayoutRequestModal earnings={earnings} onClose={(refresh) => { setPayoutOpen(false); if (refresh) onChange?.(); }} />
      )}
    </div>
  );
}

function PayoutRequestModal({ earnings, onClose }) {
  const [amount, setAmount] = useState(earnings.available_balance);
  const [method, setMethod] = useState("stripe");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/my/payouts/request", { amount: parseFloat(amount), method, note });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payout request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 flex items-center justify-center p-4" onClick={() => onClose(done)} data-testid="payout-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-headline text-2xl uppercase flex items-center gap-2"><Wallet size={18} /> Request Payout</h3>
          <button onClick={() => onClose(done)}><X size={18} /></button>
        </div>
        {done ? (
          <div className="text-center py-6" data-testid="payout-success">
            <div className="w-12 h-12 mx-auto mb-4 border border-[var(--accent)] flex items-center justify-center"><Check size={20} className="text-[var(--accent)]" /></div>
            <h4 className="font-display text-3xl mb-2 text-[var(--accent)]">PAYOUT REQUESTED</h4>
            <p className="text-neutral-300 text-sm mb-2">${Number(amount).toFixed(2)} via {method.toUpperCase()} is pending review.</p>
            <p className="font-mono-label text-[9px] text-neutral-500 mb-6">Stripe Connect rails coming soon — for now, an admin will process this manually within 3 business days.</p>
            <button onClick={() => onClose(true)} className="btn-primary">Done</button>
          </div>
        ) : (
          <>
            <p className="font-mono-label text-[10px] text-neutral-500 mb-4">
              AVAILABLE: <span className="text-[var(--accent)]">${Number(earnings.available_balance).toFixed(2)}</span> · MIN ${earnings.min_payout?.toFixed(2)}
            </p>
            <label className="font-mono-label text-[10px] text-neutral-400 mb-1 block">AMOUNT (USD)</label>
            <input
              type="number" step="0.01" min={earnings.min_payout} max={earnings.available_balance}
              className="input-brut mb-4" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="payout-amount"
            />
            <label className="font-mono-label text-[10px] text-neutral-400 mb-1 block">METHOD</label>
            <select className="input-brut mb-4" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="payout-method">
              <option value="stripe">Stripe Connect (default)</option>
              <option value="bank">Bank transfer</option>
              <option value="paypal">PayPal</option>
            </select>
            <label className="font-mono-label text-[10px] text-neutral-400 mb-1 block">NOTE (OPTIONAL)</label>
            <textarea rows={2} className="input-brut resize-none mb-5" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything we should know?" data-testid="payout-note" />
            <p className="font-mono-label text-[9px] text-neutral-500 mb-5">
              MOCKED — full Stripe Connect onboarding will be added in the next release. Request will be queued for manual review.
            </p>
            <div className="flex gap-2">
              <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="payout-submit">
                {busy ? "Submitting…" : `Request $${Number(amount || 0).toFixed(2)}`}
              </button>
              <button onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`card-brut p-5 ${accent ? "border-[var(--accent)]" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono-label text-[10px] text-neutral-400">{label}</span>
        <Icon size={14} className={accent ? "text-[var(--accent)]" : "text-neutral-500"} />
      </div>
      <div className={`font-display text-3xl ${accent ? "text-[var(--accent)]" : ""}`}>{value}</div>
      {sub && <div className="font-mono-label text-[10px] text-neutral-500 mt-2">{sub}</div>}
    </div>
  );
}

function StudioForm({ ctype, initial, onClose }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState({
    title: "", description: "", cover_image: "", file_url: "",
    external_url: "", genre: "", year: new Date().getFullYear(),
    tracklist: [], duration: "", price: 0,
    allow_downloads: true, media_kit_url: "",
    is_indy: false, tracks: [], stream_links: {},
    ...initial,
  });
  const [busy, setBusy] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingKit, setUploadingKit] = useState(false);
  const [uploadingTracks, setUploadingTracks] = useState(false);
  const coverInput = React.useRef(null);
  const fileInput  = React.useRef(null);
  const kitInput   = React.useRef(null);
  const tracksInput = React.useRef(null);
  const isAudio = ctype === "mixtapes" || ctype === "singles";
  const isVideo = ctype === "videos";
  const isBook  = ctype === "books";
  const isMixtape = ctype === "mixtapes";

  // Per-content metadata used by the uploader UI
  const mainFile = (() => {
    if (isAudio) return { kind: "audio", accept: "audio/*,.mp3,.wav,.m4a,.m4b,.aac,.ogg,.oga,.flac,.aiff,.aif,.alac,.opus,.wma,.amr,.caf,.mka,.3gp,.3gpp,.webm", label: "AUDIO FILE", spec: "ANY AUDIO · MP3/WAV/M4A/FLAC/OPUS/AAC/OGG/AIFF · UP TO 80 MB · NON-MP3 AUTO-CONVERTED", btnLabel: "Track" };
    if (isVideo) return { kind: "video", accept: "video/mp4,video/quicktime,.mp4,.mov",                                  label: "VIDEO FILE", spec: "MP4 / MOV · MAX 250 MB",   btnLabel: "Video" };
    if (isBook)  return { kind: "pdf",   accept: "application/pdf,.pdf",                                                   label: "BOOK FILE",  spec: "PDF · MAX 60 MB",         btnLabel: "PDF"   };
    return null;
  })();

  const uploadFile = async (file, kind, onDone, setLoading) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const r = await api.post("/studio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(r.data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  // Multi-track mixtape upload — accepts one ZIP or many audio files.
  // Extends whatever tracks are already staged rather than replacing them so
  // artists can drop in multiple batches.
  const uploadTracks = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingTracks(true);
    try {
      const fd = new FormData();
      Array.from(fileList).forEach((file) => fd.append("files", file));
      const r = await api.post("/studio/upload-tracks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newTracks = r.data.tracks || [];
      if (newTracks.length === 0) {
        toast.error("No playable audio found in the upload.");
        return;
      }
      setF((s) => ({ ...s, tracks: [...(s.tracks || []), ...newTracks] }));
      toast.success(`Added ${newTracks.length} track${newTracks.length > 1 ? "s" : ""}.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Track upload failed");
    } finally {
      setUploadingTracks(false);
    }
  };

  const moveTrack = (idx, dir) => {
    setF((s) => {
      const arr = [...(s.tracks || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return s;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...s, tracks: arr };
    });
  };
  const removeTrack = (idx) => setF((s) => ({ ...s, tracks: (s.tracks || []).filter((_, i) => i !== idx) }));
  const renameTrack = (idx, title) => setF((s) => ({ ...s, tracks: (s.tracks || []).map((t, i) => (i === idx ? { ...t, title } : t)) }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, year: f.year ? parseInt(f.year) : null, price: parseFloat(f.price || 0) };
      if (typeof body.tracklist === "string") {
        body.tracklist = body.tracklist.split("\n").map((s) => s.trim()).filter(Boolean);
      }
      if (isEdit) await api.put(`/my/content/${ctype}/${initial.id}`, body);
      else        await api.post(`/my/content/${ctype}`, body);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="studio-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-2xl w-full my-8">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-headline text-2xl uppercase">{isEdit ? "Edit" : "New"} {ctype.slice(0, -1)}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <input className="input-brut" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title:e.target.value })} data-testid="studio-form-title" />
          <textarea rows={3} className="input-brut resize-none" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description:e.target.value })} data-testid="studio-form-description" />
          <div className="grid sm:grid-cols-3 gap-3">
            <input className="input-brut" placeholder="Genre" value={f.genre} onChange={(e) => setF({ ...f, genre:e.target.value })} data-testid="studio-form-genre" />
            <input className="input-brut" type="number" placeholder="Year" value={f.year} onChange={(e) => setF({ ...f, year:e.target.value })} />
            <input className="input-brut" placeholder="Duration" value={f.duration} onChange={(e) => setF({ ...f, duration:e.target.value })} />
          </div>

          {/* COVER IMAGE — upload + URL */}
          <div className="border border-neutral-800 p-4">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
              <Upload size={11} /> COVER IMAGE · PNG or JPEG · MAX 8 MB
            </div>
            <div className="flex items-center gap-3 mb-2">
              {f.cover_image ? (
                <img src={resolveFileUrl(f.cover_image)} alt="" className="w-16 h-16 object-cover border border-neutral-800" data-testid="studio-form-cover-preview" />
              ) : (
                <div className="w-16 h-16 border border-dashed border-neutral-700 bg-neutral-950 flex items-center justify-center font-mono-label text-[9px] text-neutral-600">NO COVER</div>
              )}
              <input
                ref={coverInput}
                type="file"
                accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadFile(file, "image", (url) => setF((s) => ({ ...s, cover_image: url })), setUploadingCover); e.target.value = ""; }}
                data-testid="studio-form-cover-file"
              />
              <button type="button" onClick={() => coverInput.current?.click()} disabled={uploadingCover} className="btn-ghost" data-testid="studio-form-cover-upload-btn">
                <Upload size={12} /> {uploadingCover ? "Uploading…" : f.cover_image ? "Replace" : "Upload Cover"}
              </button>
              {f.cover_image && (
                <button type="button" onClick={() => setF((s) => ({ ...s, cover_image: "" }))} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="studio-form-cover-clear">REMOVE</button>
              )}
            </div>
            <input className="input-brut text-sm" placeholder="…or paste an image URL" value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} data-testid="studio-form-cover-url" />
          </div>

          {/* MAIN FILE — audio/video/book */}
          {mainFile && (
            <div className="border border-neutral-800 p-4" data-testid={`studio-form-${mainFile.kind}-card`}>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
                <Upload size={11} /> {mainFile.label} · {mainFile.spec}
              </div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                {f.file_url ? (
                  mainFile.kind === "audio" ? (
                    <audio src={resolveFileUrl(f.file_url)} controls className="h-9" data-testid="studio-form-audio-preview" />
                  ) : mainFile.kind === "video" ? (
                    <video src={resolveFileUrl(f.file_url)} controls muted className="w-32 h-20 object-cover bg-black border border-neutral-800" data-testid="studio-form-video-preview" />
                  ) : (
                    <a href={resolveFileUrl(f.file_url)} target="_blank" rel="noreferrer" className="font-mono-label text-[10px] text-[var(--accent)] underline" data-testid="studio-form-pdf-preview">VIEW PDF</a>
                  )
                ) : (
                  <div className="font-mono-label text-[10px] text-neutral-500 italic">No file loaded.</div>
                )}
                <input
                  ref={fileInput}
                  type="file"
                  accept={mainFile.accept}
                  className="hidden"
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadFile(file, mainFile.kind, (url) => setF((s) => ({ ...s, file_url: url })), setUploadingFile); e.target.value = ""; }}
                  data-testid={`studio-form-${mainFile.kind}-file`}
                />
                <button type="button" onClick={() => fileInput.current?.click()} disabled={uploadingFile} className="btn-ghost" data-testid={`studio-form-${mainFile.kind}-upload-btn`}>
                  <Upload size={12} /> {uploadingFile ? "Uploading…" : f.file_url ? `Replace ${mainFile.btnLabel}` : `Upload ${mainFile.btnLabel}`}
                </button>
                {f.file_url && (
                  <button type="button" onClick={() => setF((s) => ({ ...s, file_url: "" }))} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid={`studio-form-${mainFile.kind}-clear`}>REMOVE</button>
                )}
              </div>
              <input className="input-brut text-sm" placeholder={`…or paste a public ${mainFile.label.toLowerCase()} URL`} value={f.file_url} onChange={(e) => setF({ ...f, file_url: e.target.value })} data-testid={`studio-form-${mainFile.kind}-url`} />
              {isVideo && (
                <input className="input-brut text-sm mt-2" placeholder="…or YouTube embed URL (https://www.youtube.com/embed/…)" value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} data-testid="studio-form-external-url" />
              )}
            </div>
          )}

          {!isBook && <StreamLinksFields value={f.stream_links} onChange={(v) => setF((s) => ({ ...s, stream_links: v }))} testPrefix="studio-form" />}

          {/* MEDIA KIT / RESUME ZIP — every release type */}
          <div className="border border-neutral-800 p-4" data-testid="studio-form-kit-card">
            <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
              <Upload size={11} /> MEDIA KIT / RESUME · ZIP · MAX 200 MB · OPTIONAL
            </div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {f.media_kit_url ? (
                <a href={resolveFileUrl(f.media_kit_url)} target="_blank" rel="noreferrer" className="font-mono-label text-[10px] text-[var(--accent)] underline" data-testid="studio-form-kit-preview">DOWNLOAD CURRENT KIT</a>
              ) : (
                <div className="font-mono-label text-[10px] text-neutral-500 italic">No kit attached.</div>
              )}
              <input
                ref={kitInput}
                type="file"
                accept="application/zip,.zip"
                className="hidden"
                onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadFile(file, "zip", (url) => setF((s) => ({ ...s, media_kit_url: url })), setUploadingKit); e.target.value = ""; }}
                data-testid="studio-form-kit-file"
              />
              <button type="button" onClick={() => kitInput.current?.click()} disabled={uploadingKit} className="btn-ghost" data-testid="studio-form-kit-upload-btn">
                <Upload size={12} /> {uploadingKit ? "Uploading…" : f.media_kit_url ? "Replace Kit" : "Upload Kit (.zip)"}
              </button>
              {f.media_kit_url && (
                <button type="button" onClick={() => setF((s) => ({ ...s, media_kit_url: "" }))} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="studio-form-kit-clear">REMOVE</button>
              )}
            </div>
            <p className="font-mono-label text-[9px] text-neutral-500">STEMS · PRESS KIT · LYRICS · RESUME — ALL ZIPPED INTO ONE DOWNLOAD.</p>
          </div>

          {isMixtape && (
            <div className="border border-neutral-800 p-4" data-testid="studio-form-tracks-card">
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2 flex items-center gap-2">
                <Upload size={11} /> MIXTAPE TRACKS · DROP A .ZIP OR MULTIPLE AUDIO FILES · PLAY BACK-TO-BACK
              </div>
              <p className="font-mono-label text-[9px] text-neutral-500 mb-3">
                UPLOAD A ZIP OF AUDIO FILES OR SELECT MANY AT ONCE. ORDER = LIST ORDER (DRAG WITH ▲ / ▼).
              </p>
              <input
                ref={tracksInput}
                type="file"
                multiple
                accept=".zip,application/zip,application/x-zip-compressed,.mp3,.wav,.m4a,.aac,.ogg,.flac,.opus,.aiff,.webm,audio/*"
                className="hidden"
                onChange={(e) => { const files = e.target.files; if (files?.length) uploadTracks(files); e.target.value = ""; }}
                data-testid="studio-form-tracks-file"
              />
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => tracksInput.current?.click()}
                  disabled={uploadingTracks}
                  className="btn-ghost"
                  data-testid="studio-form-tracks-upload-btn"
                >
                  <Upload size={12} /> {uploadingTracks ? "Uploading…" : (f.tracks || []).length ? "Add More Tracks" : "Upload Tracks / ZIP"}
                </button>
                {(f.tracks || []).length > 0 && (
                  <button type="button" onClick={() => setF((s) => ({ ...s, tracks: [] }))} className="font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="studio-form-tracks-clear">
                    CLEAR ALL
                  </button>
                )}
                <span className="font-mono-label text-[10px] text-neutral-500">
                  {(f.tracks || []).length} TRACK{(f.tracks || []).length === 1 ? "" : "S"}
                </span>
              </div>
              {(f.tracks || []).length > 0 && (
                <ul className="border border-neutral-800" data-testid="studio-form-tracks-list">
                  {(f.tracks || []).map((t, i) => (
                    <li key={`${t.url}-${i}`} className="flex items-center gap-2 px-3 py-2 border-b border-neutral-800 last:border-0">
                      <span className="font-mono-label text-[10px] text-neutral-500 w-6">{(i + 1).toString().padStart(2, "0")}</span>
                      <input
                        className="input-brut flex-1 text-sm py-1"
                        value={t.title}
                        onChange={(e) => renameTrack(i, e.target.value)}
                        data-testid={`studio-form-track-title-${i}`}
                      />
                      <audio src={resolveFileUrl(t.url)} controls className="h-8" preload="none" data-testid={`studio-form-track-preview-${i}`} />
                      <button type="button" onClick={() => moveTrack(i, -1)} disabled={i === 0} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] disabled:opacity-30" data-testid={`studio-form-track-up-${i}`} aria-label="Move up">▲</button>
                      <button type="button" onClick={() => moveTrack(i, 1)} disabled={i === (f.tracks.length - 1)} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] disabled:opacity-30" data-testid={`studio-form-track-down-${i}`} aria-label="Move down">▼</button>
                      <button type="button" onClick={() => removeTrack(i)} className="font-mono-label text-[10px] px-2 py-1 border border-neutral-800 hover:border-[var(--accent)] hover:text-[var(--accent)]" data-testid={`studio-form-track-remove-${i}`} aria-label="Remove">
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {ctype === "mixtapes" && (
            <textarea rows={4} className="input-brut resize-none" placeholder="Written tracklist (one per line · optional if tracks are uploaded above)" value={Array.isArray(f.tracklist) ? f.tracklist.join("\n") : f.tracklist} onChange={(e) => setF({ ...f, tracklist:e.target.value })} data-testid="studio-form-tracklist" />
          )}

          {/* Downloads toggle */}
          <label className="flex items-center gap-3 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={f.allow_downloads !== false} onChange={(e) => setF({ ...f, allow_downloads: e.target.checked })} data-testid="studio-form-allow-downloads" />
            ALLOW FANS TO DOWNLOAD THIS RELEASE FROM YOUR PROFILE
          </label>

          {/* Indy toggle — self-declared independent release */}
          <label className="flex items-center gap-3 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
            <input type="checkbox" checked={!!f.is_indy} onChange={(e) => setF({ ...f, is_indy: e.target.checked })} data-testid="studio-form-indy" />
            TAG AS <span className="text-[var(--accent)]">INDY</span> — INDEPENDENT / UNSIGNED RELEASE
          </label>
          <div className="pt-2 flex gap-2">
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="studio-form-save">{busy ? "Saving…" : "Save"}</button>
            <button onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
          <p className="font-mono-label text-[10px] text-neutral-500 pt-2">
            Cover art (PNG/JPEG), audio (MP3/WAV/M4A), video (MP4/MOV), books (PDF), and media-kit ZIPs upload directly. OFFICIAL / FEATURED / EARLY ACCESS flags stay admin-managed.
          </p>
        </div>
      </div>
    </div>
  );
}
